import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { isDesktopQuery } from '@cx-modules/react-utils';

import { oddsButtonSizeObj } from '../components/shared-styles';

export const OFFERS_PORTAL_ID = 'offer-portal';
export const getScrollableContainerId = (marketId: number): string => `scrollable-container-${marketId}`;

export const useOffersTooltipPosition = (
  offersWidth: number,
): {
  coordinates: { x: string; y: string };
  onCardMouseEnter: () => void;
  onCardMouseLeave: () => void;
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  setShow: (show: boolean) => void;
  show: boolean;
} => {
  const [coordinates, setCoordinates] = useState<{ x: string; y: string }>({ x: '0', y: '0' });
  const [show, setShow] = useState(false);
  // `ReturnType<typeof setTimeout>` rather than `NodeJS.Timeout`: this module is consumed from source
  // by @cx-web/components, whose tsconfig has no @types/node, so the NodeJS namespace is unresolvable
  // there ("TS2503: Cannot find namespace 'NodeJS'" at its declaration emit).
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef<{ anchor: boolean; card: boolean }>({ anchor: false, card: false });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  const isDesktop = useMediaQuery(isDesktopQuery);
  const isDesktopMd = useMediaQuery({ query: `(min-width: 1200px)` });
  const cellWidth = useMemo(
    () => (isDesktop && !isDesktopMd ? oddsButtonSizeObj.widthSm : oddsButtonSizeObj.widthLg),
    [isDesktop, isDesktopMd],
  );

  const onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    isHoveringRef.current.anchor = true;

    const rect = e.currentTarget.getBoundingClientRect();
    const offerBlockHeight = 22;
    const spacing = 1;

    const viewportX = rect.left;
    const viewportY = rect.top + offerBlockHeight + spacing;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalX = viewportX;
    const remainingRightSpace = viewportWidth - viewportX;
    const remainingLeftSpace = viewportX + cellWidth;

    if (offersWidth > remainingRightSpace) {
      // Not enough space on the right, try left-aligned
      if (offersWidth <= remainingLeftSpace) {
        finalX = viewportX + cellWidth - offersWidth;
      } else {
        // Not enough space on either side, center as much as possible
        finalX = Math.max(10, Math.min(viewportX, viewportWidth - offersWidth - 10));
      }
    }

    let finalY = viewportY;
    if (viewportY + 100 > viewportHeight) {
      finalY = Math.max(10, rect.top - 100 - spacing);
    }

    // Ensure coordinates are valid numbers
    const coordX = `${Math.round(finalX)}px`;
    const coordY = `${Math.round(finalY)}px`;

    setCoordinates({ x: coordX, y: coordY });
    setShow(true);
  };

  const onMouseLeave = () => {
    isHoveringRef.current.anchor = false;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    // Debounce hide by 100ms
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current.anchor && !isHoveringRef.current.card) {
        setShow(false);
        setCoordinates({ x: '0', y: '0' });
      }
    }, 100);
  };

  const onCardMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    isHoveringRef.current.card = true;
    setShow(true);
  };

  const onCardMouseLeave = () => {
    isHoveringRef.current.card = false;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current.anchor && !isHoveringRef.current.card) {
        setShow(false);
        setCoordinates({ x: '0', y: '0' });
      }
    }, 100);
  };

  return {
    coordinates,
    onCardMouseEnter,
    onCardMouseLeave,
    onMouseEnter,
    onMouseLeave,
    setShow,
    show,
  };
};
